import { ContentCopy, NoAccounts } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Stack
} from "@mui/material";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import { logout } from "../../../SharedComponents/LoginGuard/loginGuard";
import { Pagination } from "../../../SharedComponents/Pagination";
import { SearchInput } from "../../../SharedComponents/SearchInput";
import { environment } from "../../../utils/environment";
import { calculateTotalPages } from "../../../utils/pagination";

import {
  ManagedUsersController,
  ManagedUsersControllerDelegate,
} from "../../controllers/ManagedUsersController";
import { IManagedUser } from "../../types";

import AddInstrumentModal, {
  AddInstrumentModalDelegate,
} from "../modals/AddInstrumentModal/AddInstrumentModal";
import DeleteAssignmentModal, {
  DeleteAssignmentModalDelegate,
} from "../modals/DeleteAssignmentModal/DeleteAssignmentModal";
import InviteUserModal, {
  InviteUserModalDelegate,
} from "../modals/InviteUserModal/InviteUserModal";

import EmptyState from "../../../SharedComponents/EmptyState/EmptyState";
import { withNavigationHooks } from "../../../SharedComponents/withNavigationHooks";
import InvitedUsersRow, { ManagedUserRowDelegate } from "./InvitedUsersRow";

interface State {
  data: {
    users: IManagedUser[];
    selectedUser?: string;
    deleteAssignmentData?: {
      userUid: string;
      assignmentId: number;
    };
  };
  isLoading: boolean;
  openedInviteUserModal: boolean;
  openedDeleteApplicationModal: boolean;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error" | "info" | "warning";
}

interface Props {
  navigationHooks: NavigateFunction;
}

class InvitedUsersTab
  extends React.Component<Props, State>
  implements
  ManagedUsersControllerDelegate,
  ManagedUserRowDelegate,
  AddInstrumentModalDelegate,
  InviteUserModalDelegate,
  DeleteAssignmentModalDelegate {
  private controller: ManagedUsersController;
  private filters = { filter: "", page: 1, total: 1, limit: 10 };

  constructor(props: Props) {
    super(props);
    this.controller = new ManagedUsersController(this);
    this.state = {
      data: { users: [] },
      isLoading: true,
      openedInviteUserModal: false,
      openedDeleteApplicationModal: false,
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
    };
  }

  componentDidMount() {
    this.loadData();
  }

  didGetUsers(users: IManagedUser[]) {
    this.setState({
      isLoading: false,
      data: { ...this.state.data, users },
    });
  }

  filter({
    page,
    total,
    filter,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void {
    this.filters.page = page;
    this.filters.total = total;
    this.filters.filter = filter;
  }

  applicationShouldLogout() {
    logout();
  }

  genericError(message?: string) {
    this.setState({
      isLoading: false,
      openedInviteUserModal: false,
      openedDeleteApplicationModal: false,
      snackbarOpen: true,
      snackbarMessage: message ?? "Ocorreu um erro inesperado.",
      snackbarSeverity: "error",
    });
  }

  didGetAssignmentToken(token: string) {
    this.setState({ isLoading: false });
    window.open(`${environment.feedbackBaseUrl}?jwt=${token}`, "_newtab");
  }

  didClickAddInstrument(userUid: string) {
    this.setState({
      data: { ...this.state.data, selectedUser: userUid },
    });
  }

  didClickSeeResults(assignmentId: number) {
    this.setState({ isLoading: true }, () => {
      const token = localStorage.getItem("AccessToken")!;
      this.controller.getAssignmentFeedbackToken(token, assignmentId);
    });
  }

  didClickDeleteAssignment(assignmentId: number, userUid: string) {
    this.setState({
      openedDeleteApplicationModal: true,
      data: {
        ...this.state.data,
        deleteAssignmentData: { assignmentId, userUid },
      },
    });
  }

  shouldCloseModal() {
    this.setState({
      data: { ...this.state.data, selectedUser: undefined },
      openedInviteUserModal: false,
      openedDeleteApplicationModal: false,
    });
  }

  didAddAssignments() {
    this.setState(
      {
        isLoading: true,
        data: { ...this.state.data, selectedUser: undefined },
      },
      () => this.loadData()
    );
  }

  didInviteUser() {
    this.setState({ isLoading: true, openedInviteUserModal: false }, () =>
      this.loadData()
    );
  }

  didDeleteAssignment() {
    this.setState(
      { isLoading: true, openedDeleteApplicationModal: false },
      () => this.loadData()
    );
  }

  handleOpenInvitationModal = () =>
    this.setState({ openedInviteUserModal: true });

  handleCopyInstrumentsUrl = () => {
    navigator.clipboard.writeText(environment.instrumentsFrontendBaseUrl).then(() => {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: "Link copiado com sucesso!",
        snackbarSeverity: "success",
      });
    });
  };

  handleSnackbarClose = () => this.setState({ snackbarOpen: false });

  private loadData() {
    const token = localStorage.getItem("AccessToken")!;
    const params: any = { ...this.filters };
    delete params.total;
    Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
    this.controller.getManagedUsers(token, params);
  }

  handlePageChange = (page: number) => {
    const totalPages =
      calculateTotalPages(this.filters.total, this.filters.limit) || 1;
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    if (nextPage === this.filters.page) {
      return;
    }
    this.filters.page = nextPage;
    this.loadData();
  };

  render() {
    const {
      isLoading,
      data: { users, selectedUser, deleteAssignmentData },
      openedInviteUserModal,
      openedDeleteApplicationModal,
      snackbarOpen,
      snackbarMessage,
      snackbarSeverity,
    } = this.state;

    if (isLoading && users.length === 0) {
      return (
        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box display="flex">
        <Box flex={1} overflow="auto">
          <Container maxWidth="lg"
            style={{
              padding: "40px 20px",
            }}
          >
            <Box
              marginBottom={2}
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'space-between',
                },
                flexWrap: 'wrap'
              }}
            >
              <Stack direction={"column"} spacing={1.25}>
                <Box display="flex" alignItems="center" gap={1} flexWrap={'wrap'}>
                  <Typography variant="h2">
                    Respondentes
                  </Typography>
                  
                  <Chip
                    label={`${this.filters?.total} ${this.filters?.total !== 1 ? "encontrados" : "encontrado"}`}
                    size="small" 
                  />
                </Box>

                <Typography variant="subtitle1" color={'text.secondary'}>
                  Controle e visualize a participação de respondentes convidados
                </Typography>
              </Stack>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Tooltip title={environment.instrumentsFrontendBaseUrl} arrow>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<ContentCopy />}
                    onClick={this.handleCopyInstrumentsUrl}
                  >
                    Copiar link de acesso como respondente
                  </Button>
                </Tooltip>

                <Button
                  variant="contained"
                  size="medium"
                  onClick={this.handleOpenInvitationModal}
                >
                  Convidar usuário
                </Button>
              </Box>
            </Box>
              
            <Box
              marginBottom={2}
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'start',
                  md: 'end'
                },
                flexWrap: 'wrap'
              }}
            >
              <SearchInput
                label="Pesquisar Usuário"
                onChangeFilter={(f) => (this.filters.filter = f)}
                onSearch={() => {
                  this.filters.page = 1;
                  this.loadData();
                }}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table aria-label="users-table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>N.º de Instrumentos</TableCell>
                    <TableCell align="center">Status do Convite</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <EmptyState
                          icon={NoAccounts}
                          title="Nenhum usuário encontrado"
                          description="Tente ajustar os filtros ou adicionar novos usuários."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <InvitedUsersRow key={u.uid} user={u} delegate={this} />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              currentPage={this.filters.page}
              totalPages={calculateTotalPages(
                this.filters.total,
                this.filters.limit
              )}
              onChangePage={this.handlePageChange}
            />
          </Container>
        </Box>

        {selectedUser && (
          <AddInstrumentModal userUid={selectedUser} delegate={this} />
        )}
        {openedInviteUserModal && <InviteUserModal delegate={this} />}
        {openedDeleteApplicationModal && deleteAssignmentData && (
          <DeleteAssignmentModal
            userUid={deleteAssignmentData.userUid}
            assignmentId={deleteAssignmentData.assignmentId}
            delegate={this}
          />
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={this.handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={this.handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default withNavigationHooks(InvitedUsersTab);
